import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import { useToast } from '../hooks/useToast';

const HEALTH_STYLES = {
  OK: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  CHECK: 'bg-amber-50 text-amber-900 border-amber-200',
  ERROR: 'bg-red-50 text-red-800 border-red-200',
};

/**
 * Cockpit support — point de vente / parc plat de matériels (A3).
 */
export default function CommercantPrinterCockpit({
  commercantId,
  value,
  onChange,
  /** Incrémenté par le parent après save réussi (onglet Impression) pour refermer l’éditeur de clé. */
  appKeySaveNonce = 0,
  /** true si le dernier save a envoyé une nouvelle clé (write-only). */
  appKeySavedOnLastSave = false,
}) {
  const { toast } = useToast();
  const [cockpit, setCockpit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testingLegacy, setTestingLegacy] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [journal, setJournal] = useState([]);
  const [loadingJournal, setLoadingJournal] = useState(false);
  const [forceShow, setForceShow] = useState(false);
  const [editingAppKey, setEditingAppKey] = useState(false);
  const [optimisticHasAppKey, setOptimisticHasAppKey] = useState(null);

  const localActive = Boolean(value?.imprimanteActive);

  const loadCockpit = useCallback(async () => {
    if (!commercantId) return;
    setLoading(true);
    try {
      const data = await apiService.getCommercantImprimanteCockpit(commercantId);
      setCockpit(data);
      setOptimisticHasAppKey(null);
    } catch {
      setCockpit(null);
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    loadCockpit();
  }, [loadCockpit]);

  useEffect(() => {
    if (!appKeySaveNonce) return;
    if (appKeySavedOnLastSave) setOptimisticHasAppKey(true);
    setEditingAppKey(false);
    loadCockpit();
  }, [appKeySaveNonce]); // eslint-disable-line react-hooks/exhaustive-deps — signal explicite post-save

  const devices = Array.isArray(cockpit?.devices) ? cockpit.devices : [];
  const activeDevices = devices.filter((d) => d.activeOnPointOfSale);
  const hasFleet = activeDevices.length > 0;
  const legacy = cockpit?.legacy;
  const serverLegacy = Boolean(legacy?.active || legacy?.usedAsFallback);
  const visible = localActive || serverLegacy || hasFleet || forceShow;
  const hasAppKey =
    optimisticHasAppKey !== null
      ? Boolean(optimisticHasAppKey)
      : Boolean(legacy?.hasAppKey || cockpit?.hasAppKey);

  const posHealth = cockpit?.pointOfSaleHealth || cockpit?.health;
  const health = posHealth?.health || 'CHECK';
  const displayStatus =
    posHealth?.displayStatus ||
    'État temps réel indisponible — lancez un test device pour une preuve cloud';
  const probableCause = posHealth?.probableCause;

  const patch = (partial) => {
    if (typeof onChange === 'function') onChange(partial);
  };

  const handleTestDevice = async (deviceId) => {
    if (!deviceId || testingId || testingLegacy) return;
    setTestingId(deviceId);
    try {
      const result = await apiService.testMaterielPrint(deviceId);
      if (result?.success) {
        const testId = result.testId ? ` — Test ID : ${result.testId}` : '';
        toast.success(
          result.testMode
            ? `Ticket test envoyé à l'imprimante (simulation)${testId}`
            : `Ticket test envoyé à l'imprimante${testId}`
        );
      } else {
        toast.error(result?.message || 'Échec de l’envoi du ticket test');
      }
      await loadCockpit();
    } catch (error) {
      toast.error(
        error?.data?.message || error?.message || 'Erreur lors de l’envoi du ticket test'
      );
      await loadCockpit();
    } finally {
      setTestingId(null);
    }
  };

  const handleTestLegacy = async () => {
    if (!commercantId || testingLegacy || testingId) return;
    setTestingLegacy(true);
    try {
      const result = await apiService.testCommercantImprimante(commercantId);
      if (result?.success) {
        const testId = result.testId ? ` — Test ID : ${result.testId}` : '';
        toast.success(
          result.testMode
            ? `Ticket test envoyé à l'imprimante (simulation)${testId}`
            : `Ticket test envoyé à l'imprimante${testId}`
        );
      } else {
        toast.error(result?.message || 'Échec de l’envoi du ticket test');
      }
      await loadCockpit();
    } catch (error) {
      toast.error(
        error?.data?.message || error?.message || 'Erreur lors de l’envoi du ticket test'
      );
      await loadCockpit();
    } finally {
      setTestingLegacy(false);
    }
  };

  const loadJournal = async () => {
    if (!commercantId) return;
    setLoadingJournal(true);
    try {
      const res = await apiService.getCommercantImprimanteJournal(commercantId, 20);
      setJournal(Array.isArray(res?.items) ? res.items : []);
      setShowJournal(true);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger le journal');
    } finally {
      setLoadingJournal(false);
    }
  };

  if (!visible) {
    return (
      <div className="border-t border-gray-200 pt-4 mt-2">
        <button
          type="button"
          onClick={() => {
            setForceShow(true);
            patch({ imprimanteActive: true });
          }}
          className="text-sm text-primary-700 hover:underline"
        >
          Configurer une imprimante cloud…
        </button>
        <p className="text-xs text-gray-500 mt-1">
          Optionnel — aucun impact si non configuré.
        </p>
      </div>
    );
  }

  const printCapableActive = activeDevices.filter((d) =>
    (d.capabilities || []).includes('CLOUD_PRINT')
  );

  return (
    <div className="border-t border-gray-200 pt-4 mt-2 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-md font-medium text-gray-900">
            Matériels du point de vente
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {cockpit?.fleetNote ||
              'Tous les matériels affectés à ce point de vente agissent selon leurs capacités.'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Preuves mesurées côté Clickorico — pas de statut « en ligne » constructeur.
          </p>
        </div>
        {loading ? (
          <span className="text-xs text-gray-400">Chargement…</span>
        ) : posHealth?.health ? (
          <span
            className={`text-xs font-medium px-2 py-1 rounded border ${HEALTH_STYLES[health] || HEALTH_STYLES.CHECK}`}
          >
            {health}
          </span>
        ) : null}
      </div>

      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
        <p>{displayStatus}</p>
        {probableCause && (
          <p className="mt-1 text-xs text-gray-600">
            Cause probable : {probableCause}
          </p>
        )}
        {cockpit?.mode && (
          <p className="mt-1 text-xs text-gray-500">
            Mode résolution :{' '}
            {cockpit.mode === 'DEVICES'
              ? 'parc matériel structuré'
              : cockpit.mode === 'LEGACY'
                ? 'configuration de secours'
                : 'aucune imprimante disponible'}
          </p>
        )}
      </div>

      {activeDevices.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">
            Parc affecté
          </p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
            {activeDevices.map((d) => {
              const canTest = (d.capabilities || []).includes('CLOUD_PRINT');
              return (
                <li
                  key={d.id}
                  className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {d.label || d.serialNumber}
                    </div>
                    <div className="text-xs text-gray-500">
                      {d.provider} · {d.type} · {d.status}
                      {d.capabilities?.length
                        ? ` · ${d.capabilities.join(', ')}`
                        : ''}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {d.configComplete ? 'Config cloud complète' : 'Config incomplète'}
                      {d.health?.health ? ` · ${d.health.health}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {d.health?.health && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded border ${HEALTH_STYLES[d.health.health] || HEALTH_STYLES.CHECK}`}
                      >
                        {d.health.health}
                      </span>
                    )}
                    {canTest && (
                      <button
                        type="button"
                        onClick={() => handleTestDevice(d.id)}
                        disabled={Boolean(testingId) || testingLegacy}
                        className="px-2 py-1 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-60"
                      >
                        {testingId === d.id ? 'Envoi du ticket test…' : 'Tester'}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {printCapableActive.length === 0 && (
            <p className="text-xs text-gray-500">
              Aucune imprimante cloud dans le parc affecté.
            </p>
          )}
          <Link
            to="/materiels"
            className="text-xs text-primary-700 hover:underline"
          >
            Ouvrir l’inventaire Matériels
          </Link>
        </div>
      )}

      <div className="rounded-md border border-dashed border-gray-300 bg-gray-50/80 px-3 py-3 space-y-3">
        <div className="flex items-start gap-2">
          <input
            id="impression-config-secours"
            type="checkbox"
            checked={localActive}
            onChange={(e) => patch({ imprimanteActive: e.target.checked })}
            className="mt-1 mr-1"
          />
          <div>
            <label
              htmlFor="impression-config-secours"
              className="text-sm font-medium text-gray-900 cursor-pointer"
            >
              Configuration de secours
            </label>
            <p className="text-xs text-gray-500 mt-0.5">
              Utilisée uniquement lorsqu’aucune imprimante affectée au point de
              vente n’est disponible.
            </p>
          </div>
        </div>

      {localActive && (
        <form
          autoComplete="off"
          onSubmit={(e) => e.preventDefault()}
          className="space-y-3 border-t border-gray-200 pt-3"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro de série
              </label>
              <input
                type="text"
                name="sunmi_printer_serial"
                autoComplete="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                value={value?.imprimanteNumeroSerie || ''}
                onChange={(e) => patch({ imprimanteNumeroSerie: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="SN"
              />
            </div>
            <div>
              <label
                htmlFor="sunmi-cloud-app-id"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                APP ID
              </label>
              <input
                id="sunmi-cloud-app-id"
                type="text"
                name="sunmi_cloud_app_id"
                autoComplete="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                value={value?.imprimanteAppId || ''}
                onChange={(e) => patch({ imprimanteAppId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Identifiant application cloud"
              />
            </div>
          </div>

          <div className="rounded-md border border-gray-200 bg-white px-3 py-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-gray-900">APP KEY</p>
                <p className="text-sm text-gray-700">
                  {hasAppKey ? 'Clé configurée' : 'Clé non configurée'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  La clé n’est jamais réaffichée (écriture seule).
                </p>
              </div>
              {!editingAppKey ? (
                <button
                  type="button"
                  onClick={() => {
                    setEditingAppKey(true);
                    patch({ imprimanteAppKey: '' });
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-primary-800 bg-white border border-primary-200 rounded-md hover:bg-primary-50"
                >
                  {hasAppKey ? 'Modifier la clé' : 'Configurer la clé'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditingAppKey(false);
                    patch({ imprimanteAppKey: '' });
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
              )}
            </div>
            {editingAppKey ? (
              <div>
                <label
                  htmlFor="sunmi-cloud-app-key"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {hasAppKey ? 'Nouvelle clé' : 'Clé application'}
                </label>
                <input
                  id="sunmi-cloud-app-key"
                  type="text"
                  name="sunmi_cloud_app_key"
                  autoComplete="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  value={value?.imprimanteAppKey || ''}
                  onChange={(e) => patch({ imprimanteAppKey: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={
                    hasAppKey
                      ? 'Saisir la nouvelle clé — laisser vide pour conserver'
                      : 'Saisir la clé application'
                  }
                />
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={value?.imprimerCommandeValidee !== false}
                onChange={(e) => patch({ imprimerCommandeValidee: e.target.checked })}
                className="mt-1 mr-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Imprimer dès que le client passe sa commande
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Le ticket est imprimé immédiatement après la réception de la
                  commande.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={Boolean(value?.imprimerCommandeAcceptee)}
                onChange={(e) => patch({ imprimerCommandeAcceptee: e.target.checked })}
                className="mt-1 mr-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Imprimer uniquement après avoir accepté la commande
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Le ticket est imprimé lorsque le commerçant clique sur
                  «&nbsp;Accepter&nbsp;».
                </span>
              </span>
            </label>
          </div>
        </form>
      )}
      </div>

      <div className="flex flex-wrap gap-2">
        {printCapableActive.length > 0 && (
          <button
            type="button"
            onClick={handleTestLegacy}
            disabled={testingLegacy || Boolean(testingId) || !commercantId}
            className="px-3 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-60"
          >
            {testingLegacy
              ? 'Envoi du ticket test…'
              : printCapableActive.length > 1
                ? 'Tester tous les imprimables'
                : 'Tester le parc print'}
          </button>
        )}
        {cockpit?.mode === 'LEGACY' && printCapableActive.length === 0 && (
          <button
            type="button"
            onClick={handleTestLegacy}
            disabled={testingLegacy || Boolean(testingId) || !commercantId}
            className="px-3 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-60"
          >
            {testingLegacy ? 'Envoi du ticket test…' : 'Tester la configuration de secours'}
          </button>
        )}
        <button
          type="button"
          onClick={loadJournal}
          disabled={loadingJournal || !commercantId}
          className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60"
        >
          {loadingJournal ? 'Journal…' : 'Voir le journal'}
        </button>
        <button
          type="button"
          onClick={loadCockpit}
          className="px-3 py-2 text-sm font-medium text-gray-600 hover:underline"
        >
          Rafraîchir l&apos;état
        </button>
      </div>

      {showJournal && (
        <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
          {journal.length === 0 ? (
            <p className="p-3 text-sm text-gray-500">Aucun appel enregistré.</p>
          ) : (
            <ul className="divide-y divide-gray-100 text-sm">
              {journal.map((row) => (
                <li key={row.id} className="px-3 py-2 flex justify-between gap-2">
                  <span className={row.success ? 'text-emerald-700' : 'text-red-700'}>
                    {row.success ? 'OK' : 'KO'} · {row.trigger}
                    {row.message ? ` — ${row.message}` : ''}
                  </span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleString('fr-FR')
                      : '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
